import { useEffect, useState } from "react";
import adminApi from "@/lib/adminApi";
import { fmtNaira, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock } from "lucide-react";

const STATUSES = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
];

export default function AdminLoans() {
  const [loans, setLoans] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const r = await adminApi.get(`/admin/loans${status ? `?status=${status}` : ""}`);
    setLoans(r.data.loans || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [status]);

  const override = async (loanId, transfer_status) => {
    try {
      await adminApi.post(`/admin/loans/${loanId}/override`, { transfer_status, note: `Admin override → ${transfer_status}` });
      toast.success(`Marked as ${transfer_status}`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <div className="mb-6 flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Loans</h1>
          <p className="text-sm text-kudi-ink/60">{loans.length} {status || "all"} loans</p>
        </div>
        <div className="flex gap-1 bg-white border border-border rounded-full p-1">
          {STATUSES.map((s) => (
            <button key={s.value} onClick={() => setStatus(s.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${status === s.value ? "bg-kudi-green text-white" : "text-kudi-ink/60 hover:text-kudi-ink"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-accent/40 text-xs uppercase tracking-wider text-kudi-ink/60">
              <tr>
                <th className="text-left px-4 py-3">Trader</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-right px-4 py-3">Repaid</th>
                <th className="text-left px-4 py-3">Bank</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Disbursed</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {loading ? (
                <tr><td colSpan={7} className="p-8 text-center text-kudi-ink/40">Loading...</td></tr>
              ) : loans.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-kudi-ink/40">No loans</td></tr>
              ) : loans.map((l) => (
                <tr key={l.id} data-testid={`loan-row-${l.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{l.user?.owner_name || "—"}</div>
                    <div className="text-xs text-kudi-ink/50">{l.user?.business_name}</div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">{fmtNaira(l.amount)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-kudi-ink/70">{fmtNaira(l.amount_repaid)}</td>
                  <td className="px-4 py-3 text-xs text-kudi-ink/70">{l.bank_name}<br/><span className="font-mono text-[10px] text-kudi-ink/40">{l.account_number}</span></td>
                  <td className="px-4 py-3"><StatusBadge status={l.transfer_status} /></td>
                  <td className="px-4 py-3 text-xs text-kudi-ink/50">{fmtDate(l.disbursed_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {l.transfer_status === "pending_admin" && (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => override(l.id, "success")} className="bg-emerald-100 text-emerald-700 rounded-full px-2.5 py-1 text-xs font-semibold hover:bg-emerald-200 transition flex items-center gap-1" data-testid={`approve-${l.id}`}>
                          <CheckCircle2 className="w-3 h-3" /> Mark sent
                        </button>
                        <button onClick={() => override(l.id, "failed")} className="bg-rose-100 text-rose-700 rounded-full px-2.5 py-1 text-xs font-semibold hover:bg-rose-200 transition flex items-center gap-1" data-testid={`reject-${l.id}`}>
                          <XCircle className="w-3 h-3" /> Fail
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    success: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
    pending_admin: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
    failed: { cls: "bg-rose-50 text-rose-700 border-rose-200", icon: XCircle },
    pending: { cls: "bg-blue-50 text-blue-700 border-blue-200", icon: Clock },
  };
  const c = map[status] || { cls: "bg-muted text-kudi-ink/60", icon: Clock };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full border ${c.cls}`}>
      <c.icon className="w-3 h-3" /> {status || "—"}
    </span>
  );
}
